/**
 * Adapter class used to share logic between existing codebase and new viewer. 
 */
import {Promise} from "es6-promise"; 

type visitCallback<T> = (parent: AdaptedWindow | null, window: AdaptedWindow, error?: true) => T;

interface RunOptions {
    code: string;
    win?: AdaptedWindow;
    errback?: (e: Error, code: string) => void;
}

interface AdaptedWindow extends Window {
    eval?: (script: string) => void;
}

/**
 * Visits all accessible parent windows in reverse order, optionally invoking the specified callback when
 * each such window is visited.
 *
 * @param callback - Optional callback like <tt>fn(parent: Window, window: Window, error:Error): *</tt>
 *                   to be invoked for each accessible window, which will break the traversal if a
 *                   defined value is returned. If a non accessible window is encountered, the
 *                   parent argument will be null and the error will be supplied as the third
 *                   argument. Will always be invoked at least once (perhaps will null parent) if
 *                   supplied.
 *
 * @return The top most accessible window, or the first defined value returned by <tt>callback</tt>.
 */
export function visitAdapted<T>(callback?: visitCallback<T>) {
    let win = window;
    let call = true;
    let result;

    // Note: when debugging this, Chrome will often crash if no access... :/
    while (win.parent && win.parent.window && win.parent !== win.self) {
        try {
            // Access any sensitive window property will cause an exception to be raised if no access...
            // tslint:disable-next-line:no-unused-expression
            void win.parent.innerWidth;

            if (callback) {
                result = callback(win.parent, win);
                if (typeof result !== "undefined") {
                    return result;
                }
            }

            // If here, no security error and "win.parent" never null/undefined...
            win = win.parent;

            // Already called at least once (if available), so only do so again on error after we exit this loop...
            call = false;
        } catch (e) {
            call = e;
            break;
        }
    }

    // Make sure always called at least once and on error...
    if (call && callback) {
        result = callback(null, win, call); // call = error
    }

    return typeof result === "undefined" ? win : result;
}

/**
 * Returns the correct top window reference depending on the current context (embedded or not).
 *
 * @param excludeSelf - True to return null if the top most accessible window is this window,false otherwise (default).
 * @returns A reference to the JS Window type opened, or null if the opening failed. It will expose
 *                  a <tt>close()</tt> function as per spec.
 */
export function topWindowAdapted(excludeSelf: false): AdaptedWindow;
export function topWindowAdapted(excludeSelf: true): AdaptedWindow | null;
export function topWindowAdapted(excludeSelf: boolean) {
    const win = visitAdapted(); // will default to the top most accessible window
    return excludeSelf && win === window.self ? null : win;
}

/**
 * Open a new window via the top most reachable window, either in the same window or a new window/tab,
 * ensuring a short delay before opening in the same window so stats and/or pending http requests have
 * a chance of completing if the new window/location will replace the current (client) page. <p>
 *
 * Do <b>not</b> use <tt>window.open(..)</tt> or set <tt>window.location</tt> directly, always use this
 * function! <p>
 *
 * Caveat: opening a window via <tt>setTimeout</tt> is subject to pop-up blockers, but this is the only
 * way we can ensure pending requests have a change of completing when updating the location of the
 * current (top-most) window.
 *
 * @param url    - The url to open; cannot be falsey.
 * @param target - The window target/name; will default to "_blank", i.e. new window.
 * @param specs  - Optional window specs for new windows only; can be falsey.
 *
 * @return A reference to the JS Window type opened, or null if the opening failed. It will expose
 *                  a <tt>close()</tt> function as per spec.
 */
export function openAdapted(url: string, target: string = "_blank", specs: string = "") {
    const win = topWindowAdapted(false);

    // Same window - we delay opening the url in this window a little bit so any analytics that listen for the
    // event have time to finish sending...
    if (["_self", "_parent", "_top"].indexOf(target) !== -1) {
        win.setTimeout(
            () => {
                win.location.href = url; // Caveat: this is subject to pop-up blockers
            },
            501
        ); // TODO: what about listening for SyncManager not synching instead in the Editor?

        // Same window, but will just update the location in a bit...!
        return win;
    }

    // New window/tab, will not interfere with us...
    return win.open(url, target, specs);
}

export function sandboxAdapted(path: string, scope?: Window) {
    const paths = typeof path === "string" ? [path] : path;
    let parts;
    let o;
    let i;
    let j;

    // "scope" can be null, for example if the scope is acquired via this function by an earlier call, which
    // will result in undefined being returned by this function. We differentiate between null and undefined
    // to allow a sensible default (top window) when no scope is supplied...
    if (typeof scope === "undefined") {
        scope = topWindowAdapted(false); // will be accessible!
    }

    // eslint-disable-next-line eqeqeq
    if (scope != null) {
        // Look up specific path, e.g. "window.__zmags.lightbox"?
        loop:
        for (i = 0; i < paths.length; i++) {
            // Reset to original scope for each path to traverse...
            o = scope;
            parts = paths[i].split(".");
            for (j = 0; j < parts.length; j++) {
                try {
                    // @ts-ignore
                    o = o[parts[j]];
                } catch (e) { // e.g. Security Error, TypeError (non-readable property)
                    o = null;
                    // Fall-through
                }
                // eslint-disable-next-line eqeqeq
                if (o == null) {
                    continue loop;
                }
            }
            return o;
        }
    }

    // undefined
}

export function sandboxPromiseAdapted(path: string, timeout: number, scope?: Window) {
    if (typeof scope === "number") {
        timeout = scope;
        scope = undefined; // will default to top most window
    }
    
    let outsideResolve: Function;
    let outsideReject: Function;
    const promise = new Promise((resolve, reject) => { 
        outsideResolve = resolve; 
        outsideReject = reject; 
    });

    let timer: number | null;
    let start: number;

    const clearTimer = () => {
        if (timer) {
            window.clearTimeout(timer);
            timer = null;
        }
    };

    const poll = () => {
        const result = sandboxAdapted(path, scope);
        // eslint-disable-next-line eqeqeq
        if (result != null) {
            clearTimer();
            outsideResolve(result);
            return false;
        }

        return true; // keep polling
    };

    // Do an initial poll before firing up a polling interval...
    if (poll()) {
        timeout = timeout || 5000; // 5s
        start = Date.now();

        timer = window.setInterval(
            () => {
                // "poll" will resolve and clear timer on success, we only need to reject on timeout...
                if (poll() && (Date.now() - start > timeout)) {
                    clearTimer();
                    outsideReject();
                }
            }, 
            200
        );
    }

    return promise;
}

/**
 * Runs the specified code in a given window. <p>
 *
 * <tt>options</tt> can be a string, the code to evaluate in the top most accessible window, or it can have
 * the following format for more control:
 * <pre>
 *    {
 *        code:    String,  // Mandatory code to run (eval) in <tt>options.win</tt>.
 *        win:     Window,  // Optional window to run the code in, defaulting to the top most accessible one.
 *        errback: Function // Optional errback handler to suppress errors; 
 *            if not specified, errors will be thrown as is.
 *    }
 * </pre>
 *
 * @param {String|{code:String, win:Window, errback:Function}} options
 *        If a string, the code to run (eval) in the top most accessible window; otherwise a set of options as
 *        described above; cannot be falsey.
 *
 * @return {*} The result of the evaluation, if any; can be any value.
 *
 * @throws {Error} If the run (eval) raises an error and <tt>errback</tt> is not supplied to handle it.
 */

export function runAdapted(options: string | RunOptions) {
    if (typeof options === "string") {
        options = {code: options};
    }
    const code = options.code;
    const win  = options.win || topWindowAdapted(false);

    try {
        if (win.eval) {
            return win.eval(code);
        } else {
            throw new Error("no win.eval");
        }
    } catch (e) {
        if (options.errback) {
            options.errback(e, code);
        } else {
            throw e;
        }
    }
}



// WEBPACK FOOTER //
// ./src/shared/xp/adapters/WindowAdapter.ts